import type {
  OrderChangeRow,
  OrderChangeSummary,
  PosCartItem,
  PosCartOrderChange,
  RefundedOrderItem,
} from '@/lib/services/carts/carts.types'
import type {
  SalesOrderItemChanges,
  SalesOrderRemovedItem,
} from '@/lib/services/sales-orders/sales-orders.types'

type OrderChangeCartItem = Pick<PosCartItem, 'id' | 'source_sales_order_item_id'>

export type OrderChangeLabel = 'Added' | 'Changed'

/** The parts of a POS cart the change rows are read from, so a cart in any cache shape fits. */
interface CartWithOrderChange<TItem extends OrderChangeCartItem> {
  cart_items?: TItem[] | null
  order_change?: {
    refunded_items?: RefundedOrderItem[] | null
    quote?: Partial<
      Pick<
        NonNullable<PosCartOrderChange['quote']>,
        'changes' | 'changes_document' | 'details_changed' | 'order_details_changed' | 'refund'
      >
    > | null
  } | null
}

/** A cart line, an order item the change removed, or one a refund settled, in the order the cart lists them. */
type OrderChangeListEntry<TItem> =
  | { kind: 'item'; item: TItem }
  | { kind: 'removed'; row: OrderChangeRow }
  | { kind: 'refunded'; item: RefundedOrderItem }

/**
 * How each row of an edit cart differs from the order it changes, from the server's order-change quote. Removed rows
 * follow the cart itself, so removing or restoring an item shows at once, before the server prices the change again.
 * `entries` lists the cart with each removed item crossed out where it stood, in the order the draft was seeded:
 * the order's items (add-ons after them), then anything added.
 */
export function getOrderChangeRows<TItem extends OrderChangeCartItem>(
  cart: CartWithOrderChange<TItem> | null | undefined
): {
  labelByCartItemId: Map<number, OrderChangeLabel>
  removed: OrderChangeRow[]
  entries: OrderChangeListEntry<TItem>[]
  hasChanges: boolean
} {
  const quote = cart?.order_change?.quote ?? null
  const changes = quote?.changes ?? []
  const items = cart?.cart_items ?? []
  const cartItemIds = new Set(items.map((item) => item.id))
  const sourcesInCart = new Set(items.map((item) => item.source_sales_order_item_id ?? null))
  const labelByCartItemId = new Map<number, OrderChangeLabel>()
  for (const row of changes) {
    if (row.cart_item_id === null) continue
    if (row.status === 'added') labelByCartItemId.set(row.cart_item_id, 'Added')
    else if (row.status === 'changed' || row.details_changed) {
      labelByCartItemId.set(row.cart_item_id, 'Changed')
    }
  }
  const removed = changes.filter(
    (row) =>
      row.sales_order_item_id !== null &&
      !sourcesInCart.has(row.sales_order_item_id) &&
      (row.status === 'removed' ||
        (row.cart_item_id !== null && !cartItemIds.has(row.cart_item_id)))
  )

  // Listed as the order lists them: each item by order item, its add-ons right under it, whether kept or removed.
  const parentOf = new Map(
    changes.flatMap((row) =>
      row.sales_order_item_id !== null && row.restore?.parent_sales_order_item_id
        ? [[row.sales_order_item_id, row.restore.parent_sales_order_item_id] as const]
        : []
    )
  )
  const seedPosition = (sourceId: number): [number, number] => {
    const parentId = parentOf.get(sourceId)
    return parentId === undefined ? [sourceId, 0] : [parentId, sourceId]
  }
  const fromOrder: { position: [number, number]; entry: OrderChangeListEntry<TItem> }[] = []
  for (const item of items) {
    if (item.source_sales_order_item_id) {
      fromOrder.push({
        position: seedPosition(item.source_sales_order_item_id),
        entry: { kind: 'item', item },
      })
    }
  }
  for (const row of removed) {
    if (row.sales_order_item_id !== null) {
      fromOrder.push({
        position: seedPosition(row.sales_order_item_id),
        entry: { kind: 'removed', row },
      })
    }
  }
  fromOrder.sort((a, b) => a.position[0] - b.position[0] || a.position[1] - b.position[1])
  // Items a refund settled stay on the order as the refund left them, out of the draft: listed first, where they stood.
  const refunded = cart?.order_change?.refunded_items ?? []
  const entries: OrderChangeListEntry<TItem>[] = [
    ...refunded.map((item): OrderChangeListEntry<TItem> => ({ kind: 'refunded', item })),
    ...fromOrder.map(({ entry }) => entry),
    ...items
      .filter((item) => !item.source_sales_order_item_id)
      .map((item): OrderChangeListEntry<TItem> => ({ kind: 'item', item })),
  ]
  const hasChanges =
    Boolean(quote?.refund) ||
    Boolean(quote?.changes_document) ||
    Boolean(quote?.details_changed) ||
    Boolean(quote?.order_details_changed) ||
    changes.some((row) => row.status !== 'kept') ||
    removed.length > 0 ||
    items.some((item) => !item.source_sales_order_item_id)

  return { labelByCartItemId, removed, entries, hasChanges }
}

/**
 * The removed rows that restoring `row` brings back, as the server restores them: an item comes back with its
 * removed add-ons, and an add-on with its removed item.
 */
export function getRestoredRows(removed: OrderChangeRow[], row: OrderChangeRow): OrderChangeRow[] {
  const parentId = row.restore?.parent_sales_order_item_id ?? null
  const rootId =
    parentId !== null && removed.some((other) => other.sales_order_item_id === parentId)
      ? parentId
      : row.sales_order_item_id

  return removed.filter(
    (other) =>
      other.restore &&
      (other.sales_order_item_id === rootId || other.restore.parent_sales_order_item_id === rootId)
  )
}

/** Cart lines that show restored rows until the server answers, with the ids pending lines take. */
export function getRestoredCartItems(
  rows: OrderChangeRow[],
  cartId: number,
  /** The cart writer's `nextTempLineId`. */
  nextTempLineId: () => number
): PosCartItem[] {
  return rows.flatMap((row) =>
    row.restore
      ? [
          {
            id: nextTempLineId(),
            cart_id: cartId,
            variant_id: row.restore.variant_id,
            quantity: row.restore.quantity,
            price: row.restore.price,
            variant: null,
            variant_details: row.restore.variant_details,
            source_sales_order_item_id: row.sales_order_item_id,
          },
        ]
      : []
  )
}

interface RemovedItemLine {
  id: string
  type: 'product'
  quantity: number
  amount: string
  gross_total: string
  memo: string | null
  sales_order_item_id: number
  parent_sales_order_item_id: number | null
  sales_order_item: SalesOrderRemovedItem['sales_order_item']
  remainingQuantity: 0
  returnedQuantity: number
  isRemoved: true
  removedAt: string | null
}

/** Items order changes removed, as fully returned lines so they show crossed out like returned items. */
export function getRemovedItemLines(
  order: { item_changes?: Pick<SalesOrderItemChanges, 'removed_items'> | null } | null | undefined
): RemovedItemLine[] {
  return (order?.item_changes?.removed_items ?? []).map((item, itemIndex) => ({
    id: `removed-${item.sales_order_item_id}-${itemIndex}`,
    type: 'product' as const,
    quantity: Number(item.quantity),
    amount: item.amount,
    gross_total: item.gross_total,
    memo: item.memo,
    sales_order_item_id: item.sales_order_item_id,
    parent_sales_order_item_id: item.sales_order_item?.parent_sales_order_item_id ?? null,
    sales_order_item: item.sales_order_item,
    remainingQuantity: 0 as const,
    returnedQuantity: Number(item.quantity),
    isRemoved: true as const,
    removedAt: item.removed_at,
  }))
}
